import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getCashCounts } from "@/lib/dashboardApi";
import { inr } from "@/lib/format";
import { Clock, History, User } from "lucide-react";

export default function CashCountHistoryModal({ open, onClose, token, dayDate }) {
  const [counts, setCounts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && dayDate) {
      setLoading(true);
      getCashCounts(dayDate, token)
        .then((res) => setCounts(res || []))
        .catch((err) => console.error("Failed to load cash counts:", err))
        .finally(() => setLoading(false));
    }
  }, [open, dayDate, token]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Intraday Cash Count Audit Trail</DialogTitle>
          <DialogDescription>
            Historical snapshots of physical till and safe cash counts recorded for {dayDate || "the day"}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-base">
          {loading && <p className="text-meta text-muted-foreground">Loading cash counts…</p>}

          {!loading && counts.length === 0 && (
            <div className="rounded-md border p-loose text-center text-meta text-muted-foreground">
              No cash count snapshots recorded for {dayDate || "this date"}.
            </div>
          )}

          {!loading && counts.length > 0 && (
            <div className="rounded-md border divide-y text-meta">
              {counts.map((c) => {
                const timeStr = c.created_at
                  ? new Date(c.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
                  : "—";

                return (
                  <div key={c.id} className="p-base space-y-tight">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-snug">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold text-foreground">{timeStr}</span>
                        {c.cashier_name && (
                          <span className="inline-flex items-center gap-hair text-muted-foreground text-[11px]">
                            <User className="h-3 w-3" />
                            <span>{c.cashier_name}</span>
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-base tabular-nums">
                        <div>
                          <span className="text-[11px] text-muted-foreground mr-tight">Till:</span>
                          <span className="font-bold text-foreground">
                            {c.counted_drawer !== null && c.counted_drawer !== undefined ? inr(c.counted_drawer) : "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-[11px] text-muted-foreground mr-tight">Safe:</span>
                          <span className="font-bold text-foreground">
                            {c.counted_safe !== null && c.counted_safe !== undefined ? inr(c.counted_safe) : "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {c.notes && (
                      <p className="text-[11px] text-muted-foreground pl-loose italic">
                        Note: {c.notes}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
